import { Manrope, Roboto, Roboto_Condensed, Syncopate } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import GlobalThemeSetter from "../components/context/GlobalThemeSetter";
import { AuthProvider } from '../components/context/Info';
import { avenirBook } from '../components/fonts/avenir'; // <- o arquivo acima
import { allDataHomePage } from '../services/home';
import { salvaLogs } from '../services/properties';
import '../styles/globals.css';
import '../utils/polyfills';
import { isColorLight } from '../utils/FunctionsGlobals';
import { clearLegacyHomeStorageKeys, getAllDataHomeStorageKey, setJsonStorageValue } from '../utils/homeCache';
import { runWhenIdle } from '../utils/idle';
import { isLikelyMetaPayloadText } from '../utils/isLikelyMetaPayloadText';
import { buildImoveisSeo } from '../utils/imoveisSeo';
import {
  ALLOW_MARKETING_SRC_PREFIX,
  MARKETING_SNIPPET_MARKERS,
  type MarketingSnippetMarker,
  prepareMarketingSnippets,
  renderMarketingSnippetElements,
  type PreparedMarketingSnippet,
  type SiteMarketingSettings,
} from '../utils/marketingSnippets';
import { buildCanonical, isLocalOrTestHost, normalizeHost } from '../utils/url';
// carrega o chunk da Avenir só quando for preciso

// carregue as “substitutas” com variável
const manrope = Manrope({
  subsets: ['latin'],
  weight: ['200','500', '700'], // corpo e destaque
  display: 'swap',
});

const robotoCondensed = Roboto_Condensed({ subsets: ['latin'], weight: ['300', '400', '700'], display: 'swap' });
const roboto = Roboto({ subsets: ['latin'], weight: ['300', '700'], display: 'swap' });
const syncopate = Syncopate({ subsets: ['latin'], weight: ['400', '700'], display: 'swap' });


/* ------------------------------------------------------------------
 *  _app.tsx – versão refatorada para eliminar avisos ESLint
 * ------------------------------------------------------------------ */

const EMPTY_SITE_SETTINGS: SiteMarketingSettings = {};
const ALLOW_SRC_PREFIX = ALLOW_MARKETING_SRC_PREFIX;

type IpResponse = { ip: string };

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

async function getClientIp(): Promise<string> {
  const ip6 = await fetchJson<IpResponse>('https://api6.ipify.org?format=json');
  if (ip6?.ip) return ip6.ip;
  const ip4 = await fetchJson<IpResponse>('https://api.ipify.org?format=json');
  return ip4?.ip || '0.0.0.0';
}

async function getEventId(): Promise<string> {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  const { v4 } = await import('uuid');
  return v4();
}

function toPositiveInt(value: unknown): number {
  const source = Array.isArray(value) ? value[0] : value;
  const parsed = Number(String(source ?? '').replace(/[^\d]/g, ''));
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed;
}

function isSameSiteCache(data: any, expectedHost: string, expectedCodsite: number): boolean {
  if (!data?.site?.id) return false;

  const expectedHostNormalized = normalizeHost(expectedHost);
  const cachedHostNormalized = normalizeHost(data?.site?.site);
  const stripWww = (host: string) => host.replace(/^www\./i, '');

  const codsiteMatches = expectedCodsite <= 0 || Number(data?.site?.id) === expectedCodsite;
  const hostMatches =
    !expectedHostNormalized ||
    isLocalOrTestHost(expectedHostNormalized) ||
    !cachedHostNormalized ||
    stripWww(cachedHostNormalized) === stripWww(expectedHostNormalized);

  return codsiteMatches && hostMatches;
}

function removeDynamicInjectedNodes(markers: readonly MarketingSnippetMarker[] = MARKETING_SNIPPET_MARKERS): void {
  if (typeof window === 'undefined') return;

  const activeMarkers = new Set(markers);
  document
    .querySelectorAll<HTMLElement>('[data-injected-dynamic="1"][data-injected]')
    .forEach((node) => {
      const marker = node.getAttribute('data-injected') as MarketingSnippetMarker | null;
      if (!marker || !activeMarkers.has(marker)) return;
      node.remove();
    });
}

function injectHtml(
  html: string,
  target: 'head' | 'body',
  marker: MarketingSnippetMarker
) {
  if (typeof window === 'undefined' || !html) return;

  const root = target === 'head' ? document.head : document.body;

  // limpa versões anteriores deste "grupo"
  root
    .querySelectorAll<HTMLElement>(`[data-injected="${marker}"][data-injected-dynamic="1"]`)
    .forEach((node) => node.remove());

  const container = document.createElement('div');
  container.innerHTML = html;

  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'script') {
        // bloqueia fontes não permitidas
      const s = document.createElement('script');
      // copia atributos (async, defer, src, type, etc.)
      Array.from(el.attributes).forEach((a) => s.setAttribute(a.name, a.value));
      if (el.textContent) s.text = el.textContent;
      s.setAttribute('data-injected', marker);
      s.setAttribute('data-injected-dynamic', '1');
      root.appendChild(s);
      return;
    }

    // <noscript>, <link>, <meta>, <style>, etc.
    const clone = el.cloneNode(true) as HTMLElement;
    clone.setAttribute('data-injected', marker);
    clone.setAttribute('data-injected-dynamic', '1');
    root.appendChild(clone);
  });
}

type ValidationResult = { valid: boolean; errors: string[] };

// eslint-disable-next-line no-unused-vars
function hasGtmSnippet(html: string | null | undefined): boolean {
  if (!html) return false;
  return /googletagmanager\.com\/gtm\.js|GTM-[A-Z0-9]+|dataLayer/i.test(html);
}

// eslint-disable-next-line no-unused-vars
function validateSnippet(html: string | null | undefined, expectScript = true): ValidationResult {
  if (!html || typeof html !== "string") return { valid: false, errors: ["snippet vazio"] };

  const errors: string[] = [];
  const container = document.createElement("div");
  container.innerHTML = html;

  // 1) scripts
  const scripts = Array.from(container.querySelectorAll("script"));
  if (expectScript && scripts.length === 0) {
    errors.push("nenhuma <script> encontrada");
  }

  for (const el of scripts) {
    const src = el.getAttribute("src");
    const type = (el.getAttribute("type") || "").toLowerCase();

    // ld+json é OK no <head> (não executa JS)
    if (type && type !== "text/javascript" && type !== "application/javascript" && type !== "module" && type !== "application/ld+json") {
      errors.push(`type inesperado no <script>: ${type}`);
      continue;
    }

    if (src) {
      if (!/^https:\/\//i.test(src)) errors.push(`src inválido (exige https): ${src}`);
      if (!ALLOW_SRC_PREFIX.some((p) => src.startsWith(p))) errors.push(`domínio não permitido: ${src}`);
    } else {
      const code = (el.textContent || "").trim();
      if (!code && type !== "application/ld+json") {
        errors.push("script inline vazio");
      } else if (type !== "application/ld+json") {
        try {
          // valida sintaxe sem executar
          // eslint-disable-next-line no-new-func
          new Function(code);
        } catch (e: any) {
          errors.push(`JS inline com erro de sintaxe: ${String(e?.message || e)}`);
        }
      }
    }
  }

  // 2) noscript + iframe (GTM)
  const noscriptIframes = Array.from(container.querySelectorAll("noscript iframe"));
  for (const iframe of noscriptIframes) {
    const src = iframe.getAttribute("src") || "";
    if (!ALLOW_SRC_PREFIX.some((p) => src.startsWith(p))) {
      errors.push(`<noscript><iframe> com domínio não permitido: ${src}`);
    }
  }

  // 3) tags não esperadas no snippet (endurece um pouco)
  const allowedTags = new Set(["SCRIPT", "NOSCRIPT", "LINK", "META", "STYLE", "IFRAME", "#comment"]);
  const weirdTags = Array.from(container.children).filter((el) => !allowedTags.has(el.tagName));
  if (weirdTags.length) {
    errors.push(`tags não permitidas: ${weirdTags.map((n) => n.tagName.toLowerCase()).join(", ")}`);
  }

  return { valid: errors.length === 0, errors };
}

const HIDDEN_META_PAYLOAD_ATTR = "data-hidden-meta-payload";

function isSkippableTextParent(element: Element | null): boolean {
  if (!element) return true;
  const tag = element.tagName.toUpperCase();
  return tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT" || tag === "TEXTAREA";
}

function hideMetaPayloadTextNodes(root: any): void {
  if (typeof window === "undefined") return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const toHide: Text[] = [];

  let current = walker.nextNode();
  while (current) {
    const textNode = current as Text;
    const parent = textNode.parentElement;
    const content = textNode.nodeValue || "";

    if (
      content.trim() &&
      !isSkippableTextParent(parent) &&
      isLikelyMetaPayloadText(content)
    ) {
      toHide.push(textNode);
    }

    current = walker.nextNode();
  }

  for (const textNode of toHide) {
    const parent = textNode.parentElement;
    if (!parent || isSkippableTextParent(parent)) continue;

    const hidden = document.createElement("span");
    hidden.setAttribute(HIDDEN_META_PAYLOAD_ATTR, "1");
    hidden.setAttribute("aria-hidden", "true");
    hidden.style.display = "none";
    hidden.textContent = textNode.nodeValue || "";
    parent.replaceChild(hidden, textNode);
  }
}

function RenderHeadMarketingSnippets({
  snippets
}: {
  snippets: PreparedMarketingSnippet[];
}) {
  const elements = snippets.flatMap((snippet) => renderMarketingSnippetElements(snippet));
  if (!elements.length) return null;

  return <Head>{elements}</Head>;
}

function RenderBodyMarketingSnippets({
  snippets
}: {
  snippets: PreparedMarketingSnippet[];
}) {
  if (!snippets.length) return null;

  return <>{snippets.flatMap((snippet) => renderMarketingSnippetElements(snippet))}</>;
}

function InjectMarketingSnippets({
  snippets,
  enabled
}: {
  snippets: PreparedMarketingSnippet[];
  enabled: boolean;
}) {
  useEffect(() => {
    if (!enabled) {
      removeDynamicInjectedNodes();
      return;
    }

    const activeMarkers = new Set(snippets.map((snippet) => snippet.marker));
    const inactiveMarkers = MARKETING_SNIPPET_MARKERS.filter((marker) => !activeMarkers.has(marker));
    if (inactiveMarkers.length > 0) {
      removeDynamicInjectedNodes(inactiveMarkers);
    }

    for (const snippet of snippets) {
      const hasDeclarativeMarkup = Boolean(
        document.querySelector(
          `[data-injected="${snippet.marker}"]:not([data-injected-dynamic="1"])`
        )
      );

      if (hasDeclarativeMarkup) {
        removeDynamicInjectedNodes([snippet.marker]);
        continue;
      }

      injectHtml(snippet.html, snippet.target, snippet.marker);
    }
  }, [enabled, snippets]);

  return null;
}

// Controle de pageview (gtag ou GTM)
function TrackPageView({ enabled }: { enabled: boolean }) {
  const router = useRouter();

  useEffect(() => {
    if (!enabled) return;

    const handle = (url: string) => {
      // GA4 via gtag.js (se presente)
      if (typeof window !== "undefined" && (window as any).gtag) {
        (window as any).gtag("event", "page_view", {
          page_path: url,
          page_location: window.location.origin + url,
          page_title: document.title,
        });
      }
      // GTM (se preferir ouvir event=pageview no GTM)
      if (typeof window !== "undefined" && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: "pageview",
          page_path: url,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    };

    // Não envia o primeiro PV aqui para evitar duplicidade
    router.events.on("routeChangeComplete", handle);
    return () => router.events.off("routeChangeComplete", handle);
  }, [enabled, router.events]);

  return null;
}

function TrackSite2434WhatsAppLead({ enabled }: { enabled: boolean }) {
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const whatsappTarget = target?.closest(
        [
          'a[href*="wa.me"]',
          'a[href*="api.whatsapp.com"]',
          'a[href*="web.whatsapp.com"]',
          'button[data-whatsapp]',
          '.btn-whatsapp',
          '.whatsapp',
          '[class*="whatsapp"]',
        ].join(', ')
      );

      if (whatsappTarget && typeof (window as any).fbq !== 'undefined') {
        (window as any).fbq('track', 'Lead');
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [enabled]);

  return null;
}

function MyApp({ Component, pageProps }: any) {
    /* --------------------- ESTADO ---------------------------------- */
  const router = useRouter();
    const props = pageProps || {};
    const asPath = props?.asPath || router.asPath || '/';
    const expectedHost = useMemo(() => {
      const fallbackHost =
        props?.prodOrigin ||
        props?.origemInit ||
        (typeof window !== 'undefined' ? window.location.host : '');
      return normalizeHost(fallbackHost);
    }, [props?.origemInit, props?.prodOrigin]);
    const expectedCodsite = useMemo(() => toPositiveInt(props?.codsite), [props?.codsite]);
    const allDataHomeCacheKey = useMemo(
      () => getAllDataHomeStorageKey(expectedHost, expectedCodsite),
      [expectedHost, expectedCodsite]
    );
    const canonicalUrl = useMemo(
      () => props?.canonical || buildCanonical(props?.prodOrigin, asPath || '/'),
      [props?.canonical, props?.prodOrigin, asPath]
  );
    const [allDataHome, setAllDataHome] = useState<any>(props?.allDataHome);
    const [site, setSite] = useState<any>(props?.allDataHome?.site);
    const [origem, setOrigem] = useState<any>(
        props?.origemInit || props?.prodOrigin || (typeof window !== 'undefined' ? window.location.host : '')
    );
    const notFound = props?.notFound ?? false;
    const siteCongelado = props?.siteCongelado === true || props?.erro === false || props?.allDataHome?.site?.congelado === true;
    const hasGoogleMarketingSnippetSource = Boolean(
      site?.tag_google_head ||
      site?.tag_google_body ||
      site?.google_analytics_head ||
      site?.google_analytics_body
    );
    const hasMetaPixelSource = Boolean(
      site?.pixel_facebook_head ||
      (site?.fb_token_pixel && site?.fb_id_pixel)
    );
    const initialMarketingMarkupEnabled = useRef(
      Boolean(
        props?.allDataHome?.site?.tag_google_head ||
        props?.allDataHome?.site?.tag_google_body ||
        props?.allDataHome?.site?.google_analytics_head ||
        props?.allDataHome?.site?.google_analytics_body
      )
    );
    const preparedMarketingSnippets = useMemo(
      () =>
        prepareMarketingSnippets(
          (site as SiteMarketingSettings | null) || EMPTY_SITE_SETTINGS,
          hasGoogleMarketingSnippetSource
        ),
      [hasGoogleMarketingSnippetSource, site]
    );
    const canTrackGooglePageViews = preparedMarketingSnippets.valid.length > 0;
    const shouldRenderInitialMarketingMarkup =
      initialMarketingMarkupEnabled.current && hasGoogleMarketingSnippetSource;
    const initialRenderMarketingSnippets = useMemo(
      () =>
        shouldRenderInitialMarketingMarkup
          ? preparedMarketingSnippets.valid
          : [],
      [preparedMarketingSnippets.valid, shouldRenderInitialMarketingMarkup]
    );
    const initialHeadMarketingSnippets = useMemo(
      () => initialRenderMarketingSnippets.filter((snippet) => snippet.target === 'head'),
      [initialRenderMarketingSnippets]
    );
    const initialBodyMarketingSnippets = useMemo(
      () => initialRenderMarketingSnippets.filter((snippet) => snippet.target === 'body'),
      [initialRenderMarketingSnippets]
    );

    useEffect(() => {
        if (props?.allDataHome) {
            setAllDataHome(props.allDataHome);
            setSite(props.allDataHome?.site);
        }

        const origemFromProps =
            props?.origemInit ||
            props?.prodOrigin ||
            props?.allDataHome?.site?.site;

        if (origemFromProps) {
            setOrigem(origemFromProps);
        }
    }, [props?.allDataHome, props?.origemInit, props?.prodOrigin]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        clearLegacyHomeStorageKeys(window.localStorage);
    }, []);

    useEffect(() => {
        if (!hasGoogleMarketingSnippetSource) return;

        preparedMarketingSnippets.invalid.forEach((warning) => {
            console.warn(`[inject:${warning.marker}] bloqueado:`, warning.errors);
        });
    }, [hasGoogleMarketingSnippetSource, preparedMarketingSnippets.invalid]);

    useEffect(() => {
        if(site){
            document.documentElement.style.setProperty('--cor1', site?.cor1 || '#1f1f1f');
            document.documentElement.style.setProperty('--cor2', site?.cor2 || '#77aa4d');
            document.documentElement.style.setProperty('--cor3', site?.cor3 || '#202020');
            document.documentElement.style.setProperty('--cor4', site?.cor4 || '#181818');
            document.documentElement.style.setProperty('--cor5', site?.cor5 || '#ffffff');
            document.documentElement.style.setProperty('--cor6', site?.cor6 || '#000000');
            document.documentElement.style.setProperty('--cor7', site?.cor7 || '#ffffff');
            document.documentElement.style.setProperty('--cor8', site?.cor8 || '#ffffff');
            document.documentElement.style.setProperty('--cor9', site?.cor9 || '#181818');
            document.documentElement.style.setProperty('--cor10', site?.cor10 || '#363636');
            document.documentElement.style.setProperty('--cor11', site?.cor11 || '#1a1a1a');
            document.documentElement.style.setProperty('--cor12', site?.cor12 || '#1a1a1a');
            document.documentElement.style.setProperty('--cor13', site?.cor13 || '#ffffff');
            document.documentElement.style.setProperty('--cor14', site?.cor14 || '#494949');
            document.documentElement.style.setProperty('--cor15', site?.cor15 || '#000000');
            if(isColorLight(site?.cor2)){
                document.documentElement.style.setProperty('--cor_font2', site?.cor11 || '#1a1a1a');
            } else {
                document.documentElement.style.setProperty('--cor_font2', site?.cor13 || '#ffffff');
            }
            if((isColorLight(site?.cor2) && isColorLight(site?.cor5)) || (!isColorLight(site?.cor2) && !isColorLight(site?.cor5))){
                if(isColorLight(site?.cor5)){
                    document.documentElement.style.setProperty('--cor_title', site?.cor11 || '#1a1a1a');
                } else {
                    document.documentElement.style.setProperty('--cor_title', site?.cor13 || '#ffffff');
                }
            } else {
                document.documentElement.style.setProperty('--cor_title', site?.cor2 || '#ffffff');
            }
            if(isColorLight(site?.cor5)){
                document.documentElement.style.setProperty('--cor_font', site?.cor11 || '#1a1a1a');
            } else {
                document.documentElement.style.setProperty('--cor_font', site?.cor13 || '#ffffff');
            }
            if(isColorLight(site?.cor15)){
                document.documentElement.style.setProperty('--cor_font15', site?.cor11 || '#1a1a1a');
            } else {
                document.documentElement.style.setProperty('--cor_font15', site?.cor13 || '#ffffff');
            }
        }

        if(site?.id == 1722 || site?.id == 2379){
            document.documentElement.style.setProperty(
                '--fonte-principal',
                `'avenirBook', 'avenirBook Fallback'`
            );
        }
    }, [site]);

    useEffect(() => {
        if (typeof window === "undefined") return;

        let scheduled = false;
        const run = () => {
            scheduled = false;
            hideMetaPayloadTextNodes(document.body);
        };
        const schedule = () => {
            if (scheduled) return;
            scheduled = true;
            window.requestAnimationFrame(run);
        };

        schedule();

        const observer = new MutationObserver(() => {
            schedule();
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    // 2) 404 SSR – faz o redirect de forma estável (evita hook condicional)
    useEffect(() => {
        if (notFound && asPath !== '/404') {
        router.replace('/404');
        }
    }, [notFound, asPath, router]);

    // 3) Site congelado → página de marketing
    useEffect(() => {
        if (siteCongelado) {
        router.replace('https://mkt.sobressai.com.br/congelado');
        }
    }, [siteCongelado, router]);

    /* --------------------- LOGS  ----------------------------------- */
    const salvaLog = useCallback(async () => {
        try {
        const ip = await getClientIp();
        await salvaLogs(`${origem}${asPath}`, ip, props?.codsite, origem);
        } catch {
        await salvaLogs(`${origem}${asPath}`, '0', props?.codsite, origem);
        }
    }, [asPath, origem, props?.codsite]);

    // Chama o log quando ativado
    useEffect(() => {
        if (!site?.ativa_logs || !allDataHome) return;
        const cancelIdle = runWhenIdle(() => {
            void salvaLog();
        }, 1200);
        return cancelIdle;
    }, [site?.ativa_logs, allDataHome, salvaLog]);

   // extraia a função para poder memorizá-la
    const getInfoSite = useCallback(async () => {
        try {
            const cachedRaw =
              localStorage.getItem(allDataHomeCacheKey) || localStorage.getItem('allDataHome');
            if (cachedRaw) {
                const data = JSON.parse(cachedRaw);
                if (isSameSiteCache(data, expectedHost, expectedCodsite)) {
                    setJsonStorageValue(localStorage, allDataHomeCacheKey, data);
                    localStorage.removeItem('allDataHome');
                    setAllDataHome(data);
                    setSite(data?.site);
                    setOrigem(data?.site?.site || origem || window.location.host);
                    return;
                }
            }

            const originToFetch = origem || window.location.host;
            const data = await allDataHomePage(originToFetch, props?.codsite);
            if (!data?.site?.id) return;

            setJsonStorageValue(localStorage, allDataHomeCacheKey, data);
            localStorage.removeItem('allDataHome');
            setAllDataHome(data);
            setSite(data?.site);
            setOrigem(data?.site?.site || originToFetch);
        } catch (e) {
            console.error('Erro ao buscar dados:', e);
            void salvaLog();
        }
    }, [allDataHomeCacheKey, expectedCodsite, expectedHost, origem, props?.codsite, salvaLog]);

    // efeito
    useEffect(() => {
        if (!allDataHome) {
            void getInfoSite();
            return;
        }
        setJsonStorageValue(localStorage, allDataHomeCacheKey, allDataHome);
        localStorage.removeItem('allDataHome');
    }, [allDataHome, allDataHomeCacheKey, getInfoSite]);

    const fbPixelInitialized = useRef<boolean>(false);

    useEffect(() => {
        if (!hasMetaPixelSource) return;
        if (!site?.fb_token_pixel || !site?.fb_id_pixel) return;

        const sendPageView = async () => {
            if (!site?.fb_token_pixel || !site?.fb_id_pixel) return;

            try {
                const eventId = await getEventId();
                let clientIp = '0.0.0.0';
                try {
                    clientIp = await getClientIp();
                } catch {
                    clientIp = '0.0.0.0';
                }

                const getCookie = (name: string) =>
                document.cookie.split('; ').find((r) => r.startsWith(`${name}=`))?.split('=')[1] || '';

                const fbc = getCookie('_fbc');
                const fbp = getCookie('_fbp');

                const eventTime = Math.floor(Date.now() / 1000);

                await fetch(
                    `https://graph.facebook.com/v12.0/${site.fb_id_pixel}/events?access_token=${site.fb_token_pixel}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            data: [
                                {
                                    event_name: 'PageView',
                                    event_time: eventTime,
                                    event_id: eventId,
                                    action_source: 'website',
                                    event_source_url: window.location.href,
                                    user_data: {
                                        client_ip_address: clientIp,
                                        client_user_agent: navigator.userAgent,
                                        fbc,
                                        fbp,
                                    },
                                },
                            ],
                        }),
                        keepalive: true,
                    }
                ).catch(() => undefined);

                if (site.pixel_facebook_head && !fbPixelInitialized.current) {
                const { default: ReactPixel } = await import('react-facebook-pixel');
                (ReactPixel as any).init(site.fb_id_pixel, {}, { autoConfig: false });
                fbPixelInitialized.current = true;

                (ReactPixel as any).track('PageView', {}, { eventID: eventId });

                const handleRouteChange = async () => {
                    (ReactPixel as any).track('PageView', {}, { eventID: await getEventId() });
                };

                router.events.on('routeChangeComplete', handleRouteChange);

                cleanup = () => {
                    router.events.off('routeChangeComplete', handleRouteChange);
                };
                }
            } catch {
                // Silencia falhas de rede de terceiros para n?o quebrar a navega??o.
            }
        };

        let cleanup: (() => void) | undefined;
        const cancelIdle = runWhenIdle(() => {
            void sendPageView();
        }, 1200);

        return () => {
            cancelIdle?.();
            if (cleanup) cleanup();
        };
    }, [
        hasMetaPixelSource,
        router.events,
        site?.fb_id_pixel,
        site?.fb_token_pixel,
        site?.pixel_facebook_head
    ]);

    /* --------------------- HEAD HELPERS ---------------------------- */
    function RenderHeaderDetails() {
        return null;
    }

    function RenderImoveisDetails() {
        const renderOnlyTextFilters = () => {
            let aux = ""
             const { banheiros } = router.query;
            const { dormitorios } = router.query;
            const { suites } = router.query;
            const { vagas } = router.query;
            if(dormitorios && dormitorios != undefined){
                if(dormitorios == "1")
                    aux = " com 1 dormitório"
                else
                    aux = ` com ${dormitorios} dormitórios`
            } else {
                if(suites && suites != undefined){
                    if(suites == "1")
                        aux = " com 1 suíte"
                    else
                        aux = ` com ${suites} suítes`
                } else {
                    if(banheiros && banheiros != undefined){
                        if(banheiros == "1")
                            aux = " com 1 banheiro"
                        else
                            aux = ` com ${banheiros} banheiros`
                    } else {
                        if(vagas && vagas != undefined){
                            if(vagas == "1")
                                aux = " com 1 vaga"
                            else
                                aux = ` com ${vagas} vagas`
                        }
                    }
                }
            }
            const resposta = asPath.split("/")
            if(resposta[7]){
                return `Imóvel cod: ${resposta[7]}`
            }
            if(resposta[5] && resposta[5] != "-"){
                return `${decodeURIComponent(resposta[5].replaceAll("-"," "))} ${resposta[3] && resposta[3] != "-" ? ` em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}` : ""}${aux}`
            }

            let resposta_tipo = []
            if(resposta[6])
                resposta_tipo = resposta[6].split("?")
            if(resposta_tipo[0] != "-" && resposta_tipo[0]){
                if(resposta[3] && resposta[3] != "-"){
                    if(resposta[2] == "venda"){
                        return `${decodeURIComponent(resposta_tipo[0].replaceAll("-"," ").replaceAll("Casa", "Casas").replaceAll("Terreno", "Terrenos").replaceAll("Apartamento", "Apartamentos").replaceAll("Sobrado", "Sobrados").replaceAll("Cobertura", "Coberturas").replaceAll("+",", "))} à venda em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                    } else {
                        if(resposta[2] == "aluguel"){
                            return `${decodeURIComponent(resposta_tipo[0].replaceAll("-"," ").replaceAll("Casa", "Casas").replaceAll("Terreno", "Terrenos").replaceAll("Apartamento", "Apartamentos").replaceAll("Sobrado", "Sobrados").replaceAll("Cobertura", "Coberturas").replaceAll("+",", "))} para alugar em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                        }
                        if(resposta[2] == "temporada"){
                            return `${decodeURIComponent(resposta_tipo[0].replaceAll("-"," ").replaceAll("Casa", "Casas").replaceAll("Terreno", "Terrenos").replaceAll("Apartamento", "Apartamentos").replaceAll("Sobrado", "Sobrados").replaceAll("Cobertura", "Coberturas").replaceAll("+",", "))} para temporada em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                        }
                        return `${decodeURIComponent(resposta_tipo[0].replaceAll("-"," ").replaceAll("Casa", "Casas").replaceAll("Terreno", "Terrenos").replaceAll("Apartamento", "Apartamentos").replaceAll("Sobrado", "Sobrados").replaceAll("Cobertura", "Coberturas").replaceAll("+",", "))} em ${site?.Dados.cidade ? decodeURIComponent(site?.Dados.cidade) : null}`
                    }
                }
                return `${decodeURIComponent(resposta_tipo[0].replaceAll("-"," ").replaceAll("Casa", "Casas").replaceAll("Terreno", "Terrenos").replaceAll("Apartamento", "Apartamentos").replaceAll("Sobrado", "Sobrados").replaceAll("Cobertura", "Coberturas").replaceAll("+",", "))} para ${resposta[2]}${aux}`
            }
            if(resposta[3] && resposta[3] != "-"){
                if(resposta[2] == "venda"){
                    return `Imóveis à venda em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                } else {
                    if(resposta[2] == "aluguel"){
                        return `Imóveis para alugar em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                    }
                    if(resposta[2] == "temporada"){
                        return `Imóveis para temporada em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                    }
                    return `Imóveis em ${site?.Dados.cidade ? decodeURIComponent(site?.Dados.cidade) : null}`
                }
            }
            return `Imóveis para ${resposta[2] ? resposta[2] : 'venda'}${aux}`
        };
        const bannerListagem = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "listagem");
        const urlBannerListagem = bannerListagem ? bannerListagem.length > 0 ? bannerListagem[0] : null : null;
        const capitalizeFirstLetter = (text: string) => {
            if (!text) return text;
            return text.charAt(0).toUpperCase() + text.slice(1);
        };
        const fallbackTitle = renderOnlyTextFilters() ? capitalizeFirstLetter(renderOnlyTextFilters()) : site?.titulo;
        const seo = buildImoveisSeo({
            asPath,
            site,
            allDataHome,
            query: router.query,
        });
        const headTitle = seo?.title || fallbackTitle;
        const headDescription = seo?.description || site?.meta_description || fallbackTitle;
        return (
            <Head>
                <title>{headTitle}</title>
                <link rel="preconnect" href={"https://"+site?.site}/>
                <link rel="preconnect" href={"https://www."+site?.site}/>
                <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" />
                <meta name="description" content={headDescription} />
                <meta name="twitter:title" content={headTitle} />
                <meta name="twitter:description" content={headDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="robots" content="index, follow" key="robots" />
                <meta
                    property="og:title"
                    content={headTitle}
                ></meta>
                <meta
                    property="og:description"
                    content={headDescription}
                ></meta>
                <link rel="canonical" href={canonicalUrl} key="canonical" />
                <meta
                    property="og:image"
                    content={site?.id == 2204 ? site?.url_icon : site?.id == 2280 ? site?.url_logo : site?.id == 1616 ? "https://imob.sobressai.com.br/Banner/1616/11880.jpg" : urlBannerListagem ? urlBannerListagem?.url : "https://fotos.sobressai.com.br/Banner/1722/10526.jpg"}
                ></meta>
                <meta name="twitter:image" content={site?.id == 1616 ? site?.url_icon : urlBannerListagem ? urlBannerListagem?.url : "https://fotos.sobressai.com.br/Banner/1722/10526.jpg"} />
            </Head>
        )
    }

    /* --------------------- JSX ------------------------------------- */
    if (siteCongelado || (notFound && asPath !== '/404')) {
        return (
        <div style={{ width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p>Redirecionando...</p>
        </div>
        );
    }

    const content = (
    <>
        <RenderHeadMarketingSnippets snippets={initialHeadMarketingSnippets} />
        <RenderBodyMarketingSnippets snippets={initialBodyMarketingSnippets} />
        <InjectMarketingSnippets snippets={preparedMarketingSnippets.valid} enabled={hasGoogleMarketingSnippetSource} />
        <TrackPageView enabled={canTrackGooglePageViews} />
        <TrackSite2434WhatsAppLead enabled={Number(site?.id) === 2434} />
        {props?.imovel?.id || asPath.includes("/imovel/") ? RenderHeaderDetails() : asPath.includes("/imoveis") ? RenderImoveisDetails() : null}
        <main className={`${robotoCondensed.className} ${roboto.className} ${syncopate.className} ${manrope.className}`}>
            <AuthProvider allDataHome={props?.allDataHome || allDataHome} init_props={props}>
                <GlobalThemeSetter />
                <Component {...props} />
            </AuthProvider>
        </main>
    </>
  );

  const content2 = (
    <>
        <RenderHeadMarketingSnippets snippets={initialHeadMarketingSnippets} />
        <RenderBodyMarketingSnippets snippets={initialBodyMarketingSnippets} />
        <InjectMarketingSnippets snippets={preparedMarketingSnippets.valid} enabled={hasGoogleMarketingSnippetSource} />
        <TrackPageView enabled={canTrackGooglePageViews} />
        <TrackSite2434WhatsAppLead enabled={Number(site?.id) === 2434} />
        {props?.imovel?.id || asPath.includes("/imovel/") ? RenderHeaderDetails() : asPath.includes("/imoveis") ? RenderImoveisDetails() : null}
        <main className={`${avenirBook.className}`}>
            <AuthProvider allDataHome={props?.allDataHome || allDataHome} init_props={props}>
                <GlobalThemeSetter />
                <Component {...props} />
            </AuthProvider>
        </main>
    </>
  );

  // se for o 2379, embrulha com o provider que aplica a Avenir
  return site?.id === 2379 ? content2 : content;
}

export default MyApp;





